import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../types";
import {
  FORM_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS,
  type FormFieldType,
  type FormTriggerField,
} from "../steps/form-trigger.step";

export interface FormTriggerOutputSocketSpec {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/** Re-export so existing `@erp/workflows` imports keep working. */
export { FORM_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS };

/**
 * Map a form field type to its output socket type. Files are uploaded and
 * persisted as core documents before the run starts, so the socket carries
 * a `CORE_DOCUMENT` entity reference (same shape as email attachments).
 * Entity pickers (customer/supplier) carry a typed entity reference to the
 * selected record.
 */
export function formFieldTypeToSocketType(
  fieldType: FormFieldType,
): SocketType {
  switch (fieldType) {
    case "number":
      return "number";
    case "boolean":
      return "boolean";
    case "file":
      return `entity_reference:${SystemEntity.CORE_DOCUMENT}`;
    case "customer":
      return `entity_reference:${SystemEntity.AR_CUSTOMER}`;
    case "supplier":
      return `entity_reference:${SystemEntity.AP_SUPPLIER}`;
    case "text":
    case "textarea":
    case "select":
    case "date":
    default:
      // Dates are submitted as ISO strings (YYYY-MM-DD) — mirrors how other
      // steps surface date-ish values as strings on the canvas.
      return "string";
  }
}

/**
 * Output sockets for `form_trigger`: one socket per configured field plus
 * the reserved `submission` (full payload), `submittedBy` and `submittedAt`
 * sockets. Pure + sync so the editor, the save path and the template
 * instantiation path all derive the exact same sockets from config.
 */
export function generateFormTriggerOutputSockets(
  config: Record<string, unknown>,
): FormTriggerOutputSocketSpec[] {
  const rawFields = Array.isArray(config.fields)
    ? (config.fields as Array<Partial<FormTriggerField>>)
    : [];

  const seen = new Set<string>();
  const fieldSockets: FormTriggerOutputSocketSpec[] = [];
  for (const field of rawFields) {
    if (typeof field?.id !== "string" || field.id.length === 0) continue;
    if (
      (FORM_TRIGGER_RESERVED_OUTPUT_SOCKET_IDS as readonly string[]).includes(
        field.id,
      )
    ) {
      continue;
    }
    if (seen.has(field.id)) continue;
    seen.add(field.id);

    const fieldType: FormFieldType =
      typeof field.type === "string" ? (field.type as FormFieldType) : "text";
    fieldSockets.push({
      id: field.id,
      name:
        typeof field.label === "string" && field.label.length > 0
          ? field.label
          : field.id,
      type: formFieldTypeToSocketType(fieldType),
      required: field.required === true,
      description:
        fieldType === "file"
          ? `Uploaded file for "${field.label ?? field.id}" (core document reference)`
          : fieldType === "customer"
            ? `Selected customer for "${field.label ?? field.id}" (customer reference)`
            : fieldType === "supplier"
              ? `Selected supplier for "${field.label ?? field.id}" (supplier reference)`
              : `Submitted value for "${field.label ?? field.id}"`,
    });
  }

  return [
    ...fieldSockets,
    {
      id: "submission",
      name: "Submission",
      type: "json",
      required: false,
      description:
        "Entire submission as a JSON object keyed by field id. Use this when downstream steps need the whole payload.",
    },
    {
      id: "submittedBy",
      name: "Submitted By",
      type: "string",
      required: false,
      description:
        "Email of the signed-in submitter, or empty for anonymous submissions.",
    },
    {
      id: "submittedAt",
      name: "Submitted At",
      type: "string",
      required: false,
      description: "ISO timestamp of when the form was submitted.",
    },
  ];
}
